import { SaasAdminCreationDto, SaasAdminUpdateDto } from '@docbay/schemas';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { InternalUsersActions } from 'application/constants/actions';
import { AxiosError } from 'axios';
import { InternalUsersAPI } from 'integration/api/internalUsers';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetches internal users based on the provided parameters.
 *
 * @param params - The query parameters for fetching internal users.
 * @returns A promise that resolves to the fetched data if successful, or rejects with an error message if unsuccessful.
 */
export const fetchInternalUsers = createAsyncThunk(
  InternalUsersActions.fetchInternalUsers,
  async (params: any, { rejectWithValue }) => {
    try {
      const { data } = await InternalUsersAPI.fetchInternalUsers(params);
      await delay(1000);

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
/**
 * Creates a new internal user.
 *
 * @param data - The creation data for the new internal user.
 * @returns A promise that resolves to the response data if successful, or rejects with an error message if unsuccessful.
 */
export const createInternalUser = createAsyncThunk(
  InternalUsersActions.createInternalUser,
  async (data: SaasAdminCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await InternalUsersAPI.createInternalUser(
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Edits an existing internal user.
 *
 * @param id - The ID of the internal user to be edited.
 * @param data - The update data for the internal user.
 * @returns A promise that resolves to the response data if successful, or rejects with an error message if unsuccessful.
 */
export const editInternalUser = createAsyncThunk(
  InternalUsersActions.editInternalUser,
  async (
    { id, data }: { id: string; data: SaasAdminUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await InternalUsersAPI.editInternalUser(
        id,
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an existing internal user.
 *
 * @param id - The ID of the internal user to be deleted.
 * @param type - The type of deletion operation to perform.
 * @returns A promise that resolves to the response data if successful, or rejects with an error message if unsuccessful.
 */
export const deleteInternalUser = createAsyncThunk(
  InternalUsersActions.editInternalUser,
  async ({ id, type }: { id: string; type: string }, { rejectWithValue }) => {
    try {
      const { data: responseData } = await InternalUsersAPI.deleteInternalUser(
        id,
        type,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
